/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, Button } from '@mui/material';
import appTheme from '../../common/theme/appTheme.json';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import {
  selectUserId,
  selectFlightLegToDelete,
  selectDeleteFlightLegError,
  selectDeleteFlightLegSuccess,
} from '../../../store';
import styled from 'styled-components';
import {
  clearDeleteFlightLegStatus,
  deleteFlightLeg,
  setFlightLegToDelete,
  showDeleteLegConfirmation,
} from '../../../store/flightLegs/actions';

export interface DeleteModalProps {}

const StyledAvatar = styled(Avatar)`
  width: 75px;
  height: 75px;
  background-color: ${appTheme.primaryTextColor};
  color: ${appTheme.mptPrimaryBackgroundColor};
  font-size: 30px;
  margin: auto;
`;

const StyledWrapper = styled(Box)`
  width: 340px;
  height: 240px;
  display: flex;
  flex-direction: column;
  border-radius: 15px;
`;

const StyledTop = styled(Box)`
  flex-basis: 25%;
  background-color: ${appTheme.secondaryBackgroundColor};
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px 10px 0px 0px;
  word-break: break-word;
  padding: 3px 6px 0px 6px;
`;

const StyledMiddle = styled(Box)`
  flex-basis: 50%;
  background-color: ${appTheme.secondaryBackgroundColor};
  color: white;
  font-size: 17px;
  display: flex;
  align-items: center;
  text-align: center;
  word-break: break-word;
  padding: 0px 6px 0px 6px;
`;

const StyledBottom = styled(Box)`
  flex-basis: 25%;
  background-color: ${appTheme.secondaryBackgroundColor};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0px 0px 10px 10px;
`;

const StyledDeleteButton = {
  width: '31%',
  borderRadius: '18px',
  backgroundColor: appTheme.mptLightBlue,
  color: appTheme.darkGrayTextColor,
  fontWeight: 500,
  marginLeft: '2%',
  '&:hover': {
    backgroundColor: appTheme.mptLightBlue,
  },
};

const StyledCancelButton = {
  width: '31%',
  borderRadius: '18px',
  borderColor: appTheme.primaryTextColor,
  backgroundColor: appTheme.mptPrimaryBackgroundColor,
  fontWeight: 500,
  marginRight: '2%',
  '&:hover': {
    backgroundColor: appTheme.mptPrimaryBackgroundColor,
  },
};

const DeleteLegModal = (props: DeleteModalProps) => {
  const userId = useSelector(selectUserId, shallowEqual);
  const deletingError = useSelector(selectDeleteFlightLegError, shallowEqual);
  const deletingSuccess = useSelector(selectDeleteFlightLegSuccess, shallowEqual);
  const legToDelete = useSelector(selectFlightLegToDelete, shallowEqual);
  const dispatch = useDispatch();
  const [header, setHeader] = useState<String>('Delete Flight Leg?');
  const [deleteDisabled, setDeleteDisabled] = useState<boolean>(false);

  // On successful deletion
  useEffect(() => {
    if (deletingSuccess) {
      dispatch(setFlightLegToDelete(undefined));
      dispatch(showDeleteLegConfirmation(false));
      dispatch(clearDeleteFlightLegStatus());
    }
  }, [deletingSuccess, dispatch, userId]);

  // On unsuccessful deletion
  useEffect(() => {
    if (deletingError) {
      setHeader('Something went wrong');
      setDeleteDisabled(true);
    }
  }, [deletingError, dispatch]);

  // Cancel button press
  const handleCancel = useCallback(() => {
    dispatch(showDeleteLegConfirmation(false));
    setDeleteDisabled(false);
  }, [dispatch]);

  // Delete button press
  const handleDelete = useCallback(() => {
    if (userId && legToDelete) {
      dispatch(deleteFlightLeg(userId, legToDelete.id));
    } else {
      setHeader('Something went wrong');
      setDeleteDisabled(true);
    }
  }, [userId, legToDelete, dispatch]);

  return (
    <div className="modal">
      <StyledWrapper>
        <StyledTop>{header}</StyledTop>
        <StyledMiddle>
          <StyledAvatar>{legToDelete ? legToDelete.legOrder : null}</StyledAvatar>
        </StyledMiddle>
        <StyledBottom>
          <Button color="primary" sx={{ ...StyledCancelButton }} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            sx={{ ...StyledDeleteButton }}
            onClick={() => {
              handleDelete();
            }}
            disabled={deleteDisabled}
          >
            Delete
          </Button>
        </StyledBottom>
      </StyledWrapper>
    </div>
  );
};

export default DeleteLegModal;
